import { useEffect, useRef, useState } from 'react';
import type { Task, TaskStatus } from '@/types/api';
import { BOARD_COLUMNS } from './constants';
import { TaskCard } from '@/features/tasks/TaskCard';
import { EmptyState } from '@/components/EmptyState';
import { TaskCardSkeleton } from '@/components/Skeleton';
import { LoadMoreSentinel } from './LoadMoreSentinel';
import type { BoardView } from './useBoardColumns';
import { ColumnFilterMenu } from './ColumnFilterMenu';
import type { ColumnFilter } from '@/types/api';

/**
 * Five columns do not fit a 390px phone, so: scroll-snap panes, and a chip rail.
 *
 * There is no drag, on any device — people do not move cards at all. Tapping a card
 * opens its story; the desks move it.
 *
 * CSS scroll-snap gives native momentum and 60fps with no gesture code. The chip rail
 * and the swipe are two controls over one piece of state, and the swipe updates the
 * rail through IntersectionObserver rather than scroll-offset arithmetic.
 */
export function MobileBoard({
  board,
  onOpen,
  showAssignee = false,
  onFilter,
}: {
  board: BoardView;
  onOpen?: ((task: Task) => void) | undefined;
  showAssignee?: boolean;
  onFilter?: ((status: TaskStatus, filter: ColumnFilter) => void) | undefined;
}) {
  const byStatus = new Map(board.columns.map((c) => [c.status, c]));

  /*
    Order comes from the CONSTANT, membership from the DATA.
    Iterating BOARD_COLUMNS alone would render all five whatever the filter said;
    iterating board.columns alone would let the server decide the order. Filtering one
    by the other keeps both properties.
  */
  const columns = BOARD_COLUMNS.filter((column) => byStatus.has(column.status));

  /*
    Tag options come from the cards actually on the board, not the desk vocabulary.
    An option that matches nothing is a dead end you have to try before you learn that.
  */
  const tags = [...new Set(board.columns.flatMap((c) => c.tasks).map((t) => t.tag).filter(Boolean))]
    .sort() as string[];
  const scrollerRef = useRef<HTMLDivElement>(null);
  const paneRefs = useRef(new Map<TaskStatus, HTMLElement>());
  const railRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef(new Map<TaskStatus, HTMLElement>());
  const [active, setActive] = useState<TaskStatus>('pending');

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const status = visible?.target.getAttribute('data-status') as TaskStatus | null;
        if (status) setActive(status);
      },
      { root: scroller, threshold: [0.5, 0.75] },
    );

    for (const pane of paneRefs.current.values()) observer.observe(pane);
    return () => observer.disconnect();
  }, [board.columns.length]);

  /**
   * The rail follows the swipe.
   *
   * Five chips do not fit a phone, so swiping to Completed left the active chip off
   * the right edge — the one control telling you where you are was the one you could
   * not see.
   *
   * scrollLeft on the rail itself, not scrollIntoView: that walks up the ancestors and
   * would scroll the PANE container sideways too, dragging you to a different column
   * as a side effect of arriving at this one.
   */
  useEffect(() => {
    const rail = railRef.current;
    const chip = chipRefs.current.get(active);
    if (!rail || !chip) return;

    const target = chip.offsetLeft - (rail.clientWidth - chip.offsetWidth) / 2;
    rail.scrollTo({
      left: Math.max(0, Math.min(target, rail.scrollWidth - rail.clientWidth)),
      behavior: 'smooth',
    });
  }, [active]);

  function goTo(status: TaskStatus) {
    paneRefs.current.get(status)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={railRef}
        className="flex shrink-0 gap-2 overflow-x-auto no-scrollbar border-b px-3 py-2"
        style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
        role="tablist"
        aria-label="Board columns"
      >
        {columns.map((column) => {
          const isActive = column.status === active;
          return (
            <button
              key={column.status}
              ref={(node) => {
                if (node) chipRefs.current.set(column.status, node);
                else chipRefs.current.delete(column.status);
              }}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => goTo(column.status)}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors"
              style={{
                borderColor: isActive ? column.colorVar : 'var(--border)',
                background: isActive ? column.softVar : 'transparent',
                color: isActive ? 'var(--text)' : 'var(--text-muted)',
              }}
            >
              <column.icon size={13} style={{ color: column.colorVar }} />
              {column.label}
              <span className="tabular-nums opacity-70">{byStatus.get(column.status)?.total ?? 0}</span>
              {(byStatus.get(column.status)?.unread ?? 0) > 0 && (
                <span
                  className="size-1.5 rounded-full"
                  style={{ background: 'var(--accent)' }}
                  aria-label="has new activity"
                />
              )}
              {byStatus.get(column.status)?.filtered && (
                <span
                  className="size-1.5 rounded-full"
                  style={{ background: 'var(--accent)' }}
                  aria-label="filtered"
                  title="Filtered"
                />
              )}
            </button>
          );
        })}
      </div>

      <div
        ref={scrollerRef}
        className="flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto overscroll-x-contain no-scrollbar"
      >
        {columns.map((column) => {
          const data = byStatus.get(column.status);
          const tasks = data?.tasks ?? [];

          return (
            <section
              key={column.status}
              data-status={column.status}
              ref={(node) => {
                if (node) paneRefs.current.set(column.status, node);
                else paneRefs.current.delete(column.status);
              }}
              // w-full, not w-screen: 100vw counts the scrollbar the container does not
              // have, so the panes were fractionally wider than the space they snap in.
              className="flex w-full min-h-0 shrink-0 snap-center flex-col gap-2 overflow-y-auto overscroll-y-contain no-scrollbar p-3"
              style={{
                // The home indicator and the browser's bottom chrome both sit over the
                // last card. Padding rather than a spacer, so it collapses to 0.75rem
                // on anything without a notch.
                paddingBottom: 'max(0.75rem, calc(env(safe-area-inset-bottom) + 0.75rem))',
              }}
            >
              {onFilter && data && (
                <div className="flex items-center justify-between px-1 pb-1">
                  <span className="text-[11px]" style={{ color: 'var(--text-faint)' }}>
                    {data.filtered ? `${data.total} filtered` : `${data.total} ${column.label.toLowerCase()}`}
                  </span>
                  <ColumnFilterMenu
                    filter={data.filter}
                    tags={tags}
                    onApply={(next) => onFilter(column.status, next)}
                  />
                </div>
              )}

              {data?.loading ? (
                <>
                  <TaskCardSkeleton />
                  <TaskCardSkeleton />
                  <TaskCardSkeleton lines={1} />
                </>
              ) : tasks.length === 0 ? (
                // A phone pane is a whole screen, so a single line of text in it reads
                // as a rendering failure.
                <EmptyState
                  size="sm"
                  title={data?.filtered ? 'Nothing matches this filter.' : column.emptyMessage}
                />
              ) : (
                tasks.map((task) => (
                  <TaskCard key={task.id} task={task} onOpen={onOpen} showAssignee={showAssignee} />
                ))
              )}

              {data?.hasMore && (
                <LoadMoreSentinel
                  onVisible={data.loadMore}
                  loading={data.isLoadingMore}
                  remaining={data.total - tasks.length}
                />
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
